import { AppWindow, Maximize2, Minimize2, Minus, PanelLeft, PanelRight, X } from 'lucide-react';
import { useRef, useState, type KeyboardEventHandler, type PointerEvent as ReactPointerEvent, type ReactNode } from 'react';
import { cx } from '../utils.ts';
import { IconButton } from './Button.tsx';
import { Menu, type MenuItem } from './Menu.tsx';
import './window.css';

/* ---------------------------------------------------------------------------------------------
 * Geometry. Boxes are px relative to the desk the windows sit on. Snaps are fractions of the
 * desk, so a snapped window follows the desk's size.
 * ------------------------------------------------------------------------------------------- */

export interface WindowRect {
  x: number;
  y: number;
  w: number;
  h: number;
}
/** A slot on the desk, as fractions (0–1) of its size. */
export type WindowSnap = WindowRect;
export interface DeskSize {
  width: number;
  height: number;
}
/** Where a window ends up after a drag or resize: a free box, or a slot to fill. */
export type WindowPlace = { rect: WindowRect } | { snap: WindowSnap };
export type WindowEdge = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** Space around the desk edge and between snapped windows. */
export const WINDOW_GAP = 8;
export const WINDOW_MIN = { w: 320, h: 200 };
/** How much of a free window stays on the desk: enough of the title bar to grab it again. */
const KEEP = 96;
const BAR = 40;
/** A dragged window snaps within this distance (px) of a desk edge; the corners snap to quarters. */
const EDGE = 16;
const CORNER = 72;
const EDGES: WindowEdge[] = ['n', 's', 'e', 'w', 'ne', 'nw', 'se', 'sw'];

export const SNAPS = {
  max: { x: 0, y: 0, w: 1, h: 1 },
  left: { x: 0, y: 0, w: 0.5, h: 1 },
  right: { x: 0.5, y: 0, w: 0.5, h: 1 },
  topLeft: { x: 0, y: 0, w: 0.5, h: 0.5 },
  topRight: { x: 0.5, y: 0, w: 0.5, h: 0.5 },
  bottomLeft: { x: 0, y: 0.5, w: 0.5, h: 0.5 },
  bottomRight: { x: 0.5, y: 0.5, w: 0.5, h: 0.5 },
} as const satisfies Record<string, WindowSnap>;

export const isMaxSnap = (snap: WindowSnap | null | undefined): boolean => !!snap && snap.x === 0 && snap.y === 0 && snap.w === 1 && snap.h === 1;

const clamp = (v: number, lo: number, hi: number) => Math.min(Math.max(v, lo), Math.max(lo, hi));

/** The box a slot fills on a desk of this size. */
export function snapRect(snap: WindowSnap, desk: DeskSize, gap = WINDOW_GAP): WindowRect {
  const w = desk.width - gap;
  const h = desk.height - gap;
  return { x: Math.round(gap + snap.x * w), y: Math.round(gap + snap.y * h), w: Math.round(snap.w * w - gap), h: Math.round(snap.h * h - gap) };
}

/** Fits a free window to the desk: no bigger than it, title bar on it, and enough in view to grab. */
export function clampRect(rect: WindowRect, desk: DeskSize): WindowRect {
  if (!desk.width || !desk.height) return rect;
  const w = Math.min(Math.max(rect.w, WINDOW_MIN.w), desk.width);
  const h = Math.min(Math.max(rect.h, WINDOW_MIN.h), desk.height);
  return { x: clamp(rect.x, KEEP - w, desk.width - KEEP), y: clamp(rect.y, 0, desk.height - BAR), w, h };
}

/** A box resized by dragging one edge or corner, kept above the minimum size and inside the desk. */
export function resizeRect(start: WindowRect, edge: WindowEdge, dx: number, dy: number, desk: DeskSize): WindowRect {
  let { x, y, w, h } = start;
  if (edge.includes('e')) w = clamp(start.w + dx, WINDOW_MIN.w, Math.max(desk.width - start.x, start.w));
  if (edge.includes('s')) h = clamp(start.h + dy, WINDOW_MIN.h, Math.max(desk.height - start.y, start.h));
  if (edge.includes('w')) {
    x = clamp(start.x + dx, Math.min(0, start.x), start.x + start.w - WINDOW_MIN.w);
    w = start.w + start.x - x;
  }
  if (edge.includes('n')) {
    y = clamp(start.y + dy, Math.min(0, start.y), start.y + start.h - WINDOW_MIN.h);
    h = start.h + start.y - y;
  }
  return { x, y, w, h };
}

/** The slot under a point dragged to a desk edge: a half, a quarter in the corners, the whole desk at the top. */
export function snapAt(x: number, y: number, desk: DeskSize): WindowSnap | null {
  const left = x < EDGE;
  const right = x > desk.width - EDGE;
  if (left || right) {
    if (y < CORNER) return left ? SNAPS.topLeft : SNAPS.topRight;
    if (y > desk.height - CORNER) return left ? SNAPS.bottomLeft : SNAPS.bottomRight;
    return left ? SNAPS.left : SNAPS.right;
  }
  return y < EDGE ? SNAPS.max : null;
}

/** Slots for n windows side by side: columns up to three, then a grid. */
export function tileSnaps(n: number): WindowSnap[] {
  if (n <= 3) return Array.from({ length: Math.max(n, 0) }, (_, i) => ({ x: i / n, y: 0, w: 1 / n, h: 1 }));
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  const out: WindowSnap[] = [];
  for (let r = 0; r < rows; r++) {
    const inRow = r < rows - 1 ? cols : n - cols * (rows - 1);
    for (let c = 0; c < inRow; c++) out.push({ x: c / inRow, y: r / rows, w: 1 / inRow, h: 1 / rows });
  }
  return out;
}

/* ---------------------------------------------------------------------------------------------
 * Window: a movable, resizable panel on a desk (an absolutely positioned parent). The owner keeps
 * the geometry; the window reports where a drag or resize ends.
 * ------------------------------------------------------------------------------------------- */

export interface WindowProps {
  /** The box on the desk now. A snapped window passes the box its slot fills. */
  rect: WindowRect;
  /** The size a snapped window returns to when it's dragged loose. */
  restoreSize?: { w: number; h: number };
  /** Fills a slot (a half, a quarter…) instead of floating. */
  snapped?: boolean;
  maximized?: boolean;
  desk: DeskSize;
  active?: boolean;
  zIndex?: number;
  /** Drag, resize and snap. Off where windows fill the desk (phones). */
  movable?: boolean;
  hidden?: boolean;
  title: ReactNode;
  icon?: ReactNode;
  /** Accessible name. */
  label: string;
  /** Controls placed before minimize, maximize and close. */
  actions?: ReactNode;
  className?: string;
  children: ReactNode;
  /** Pressed or focused: the owner brings it to the front. */
  onActivate?: () => void;
  onClose: () => void;
  onMinimize?: () => void;
  onToggleMaximize?: () => void;
  onPlace?: (place: WindowPlace) => void;
  /** The slot under the pointer while dragging (null away from the edges), for the desk to preview. */
  onSnapPreview?: (snap: WindowSnap | null) => void;
  onKeyDown?: KeyboardEventHandler<HTMLDivElement>;
}

interface Gesture {
  kind: 'move' | WindowEdge;
  pointerId: number;
  /** Pointer at the start, in client coordinates. */
  x: number;
  y: number;
  start: WindowRect;
  desk: DOMRect;
  moved: boolean;
  rect: WindowRect;
  snap: WindowSnap | null;
}

const isControl = (target: EventTarget) => target instanceof Element && !!target.closest('button, a, input, textarea, select');

export function Window({
  rect,
  restoreSize,
  snapped,
  maximized,
  desk,
  active,
  zIndex,
  movable = true,
  hidden,
  title,
  icon,
  label,
  actions,
  className,
  children,
  onActivate,
  onClose,
  onMinimize,
  onToggleMaximize,
  onPlace,
  onSnapPreview,
  onKeyDown,
}: WindowProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const gesture = useRef<Gesture | null>(null);
  // The box while a drag or resize is under way; the owner only hears where it ends.
  const [live, setLive] = useState<WindowRect | null>(null);
  const box = live ?? rect;

  const begin = (kind: Gesture['kind'], e: ReactPointerEvent<HTMLElement>) => {
    const deskEl = rootRef.current?.offsetParent;
    if (!movable || e.button !== 0 || !deskEl) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    gesture.current = { kind, pointerId: e.pointerId, x: e.clientX, y: e.clientY, start: rect, desk: deskEl.getBoundingClientRect(), moved: false, rect, snap: null };
  };

  const move = (e: ReactPointerEvent<HTMLElement>) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    const dx = e.clientX - g.x;
    const dy = e.clientY - g.y;
    if (!g.moved) {
      // A few px of slack keeps clicks and double-clicks on the title bar from moving the window.
      if (Math.abs(dx) + Math.abs(dy) < 4) return;
      g.moved = true;
      // A snapped window comes loose at its own size, still held where it was grabbed.
      if (g.kind === 'move' && (snapped || maximized) && restoreSize) {
        const grab = (g.x - g.desk.left - g.start.x) / g.start.w;
        g.start = { x: g.x - g.desk.left - grab * restoreSize.w, y: g.start.y, w: restoreSize.w, h: restoreSize.h };
      }
    }
    if (g.kind === 'move') {
      g.rect = { ...g.start, x: Math.round(g.start.x + dx), y: Math.max(0, Math.round(g.start.y + dy)) };
      const snap = snapAt(e.clientX - g.desk.left, e.clientY - g.desk.top, desk);
      if (snap !== g.snap) {
        g.snap = snap;
        onSnapPreview?.(snap);
      }
    } else {
      g.rect = resizeRect(g.start, g.kind, Math.round(dx), Math.round(dy), desk);
    }
    setLive(g.rect);
  };

  const end = (e: ReactPointerEvent<HTMLElement>, commit: boolean) => {
    const g = gesture.current;
    if (!g || g.pointerId !== e.pointerId) return;
    gesture.current = null;
    if (g.moved) {
      if (g.snap) onSnapPreview?.(null);
      if (commit) onPlace?.(g.snap ? { snap: g.snap } : { rect: clampRect(g.rect, desk) });
    }
    setLive(null);
  };

  const gestureHandlers = {
    onPointerMove: move,
    onPointerUp: (e: ReactPointerEvent<HTMLElement>) => end(e, true),
    onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => end(e, false),
  };

  const menu: MenuItem[] = [];
  if (movable && onToggleMaximize) menu.push({ label: maximized ? 'Restore' : 'Maximize', icon: maximized ? <Minimize2 /> : <Maximize2 />, onSelect: onToggleMaximize });
  if (movable && onPlace) {
    menu.push({ label: 'Snap left', icon: <PanelLeft />, onSelect: () => onPlace({ snap: SNAPS.left }) });
    menu.push({ label: 'Snap right', icon: <PanelRight />, onSelect: () => onPlace({ snap: SNAPS.right }) });
  }
  if (onMinimize) menu.push({ label: 'Minimize', icon: <Minus />, onSelect: onMinimize });
  if (menu.length) menu.push({ type: 'separator' });
  menu.push({ label: 'Close', icon: <X />, onSelect: onClose });

  return (
    <div
      ref={rootRef}
      role="dialog"
      aria-label={label}
      // Clicking the chrome focuses the window, so its keys (Escape…) reach it.
      tabIndex={-1}
      className={cx('g-window', className)}
      data-state={maximized ? 'max' : snapped ? 'snapped' : 'free'}
      data-active={active || undefined}
      data-moving={live ? '' : undefined}
      hidden={hidden}
      style={{ left: box.x, top: box.y, width: box.w, height: box.h, zIndex }}
      onPointerDownCapture={onActivate}
      onFocusCapture={onActivate}
      onKeyDown={onKeyDown}
    >
      <div className="g-window__frame">
        <header
          className="g-window__bar"
          onPointerDown={(e) => !isControl(e.target) && begin('move', e)}
          onDoubleClick={(e) => movable && !isControl(e.target) && onToggleMaximize?.()}
          {...gestureHandlers}
        >
          <Menu
            items={menu}
            trigger={
              <button type="button" className="g-window__icon" aria-label="Window menu">
                {icon ?? <AppWindow />}
              </button>
            }
          />
          <div className="g-window__title">{title}</div>
          {actions && <div className="g-window__actions">{actions}</div>}
          <div className="g-window__controls">
            {onMinimize && (
              <IconButton label="Minimize" size="sm" onClick={onMinimize}>
                <Minus />
              </IconButton>
            )}
            {movable && onToggleMaximize && (
              <IconButton label={maximized ? 'Restore' : 'Maximize'} size="sm" onClick={onToggleMaximize}>
                {maximized ? <Minimize2 /> : <Maximize2 />}
              </IconButton>
            )}
            <IconButton label="Close" size="sm" onClick={onClose}>
              <X />
            </IconButton>
          </div>
        </header>
        <div className="g-window__body">{children}</div>
      </div>
      {movable && !maximized && EDGES.map((edge) => <div key={edge} className="g-window__edge" data-edge={edge} onPointerDown={(e) => begin(edge, e)} {...gestureHandlers} />)}
    </div>
  );
}

/** Where a dragged window will land. */
export function WindowPreview({ rect, zIndex }: { rect: WindowRect; zIndex?: number }) {
  return <div className="g-window-preview" style={{ left: rect.x, top: rect.y, width: rect.w, height: rect.h, zIndex }} />;
}
